import type { MouseEventHandler, ReactNode } from 'react'
import { Link } from 'react-router'
import { cx } from '../cx'
import { Icon, type IconName } from './Icon'
import s from './Button.module.css'

/**
 * A button, or a link that looks like one: `to` for a page of this app (no reload), `href` for anywhere else. Primary is ink and there is at most one per view; secondary is
 * the default; ghost sits in toolbars and headers. `icon` with `iconOnly` draws a square button: give it a `label`.
 * Every button is at least 44px tall on touch screens.
 */
export function Button(props: {
  children?: ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md'
  icon?: IconName
  /** Only the icon shows; `label` becomes its accessible name and tooltip. */
  iconOnly?: boolean
  label?: string
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  href?: string
  to?: string
  type?: 'button' | 'submit'
  disabled?: boolean
  /** Pressed state, for toggles. */
  pressed?: boolean
}) {
  const className = cx(
    s.button,
    s[props.variant ?? 'secondary'],
    s[props.size ?? 'md'],
    props.iconOnly && s.iconOnly,
  )
  const content = (
    <>
      {props.icon && <Icon name={props.icon} />}
      {props.iconOnly ? null : props.children}
    </>
  )
  const name = props.iconOnly ? (props.label ?? String(props.children ?? '')) : props.label
  if (props.to) {
    return (
      <Link className={className} to={props.to} aria-label={name} title={props.iconOnly ? name : undefined}>
        {content}
      </Link>
    )
  }
  if (props.href) {
    return (
      <a className={className} href={props.href} onClick={props.onClick} aria-label={name} title={props.iconOnly ? name : undefined}>
        {content}
      </a>
    )
  }
  return (
    <button
      className={className}
      type={props.type ?? 'button'}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={name}
      aria-pressed={props.pressed}
      title={props.iconOnly ? name : undefined}
    >
      {content}
    </button>
  )
}
